/**
 * Trainer Dashboard
 *
 * Personalized trainer experience per VISION.md:
 * - Warm welcome with name
 * - Recent assessments at a glance
 * - Quick actions to create new assessments
 * - No technical jargon (tenant IDs, webhooks, etc.)
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import { apiClient } from '../../lib/api';
import type { Assessment } from '../../lib/types';

export function TrainerDashboard() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Derive display name from user
  const displayName = user?.name || user?.email?.split('@')[0] || 'Trainer';
  const firstName = displayName.split(' ')[0];

  // Greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Load recent assessments
  const loadAssessments = async () => {
    setLoadingAssessments(true);
    setLoadError(null);
    try {
      const res = await apiClient.listAssessments({ limit: 5 });
      if (res.response.data) {
        setAssessments(res.response.data.items);
      } else if (res.response.error) {
        setLoadError(res.response.error.message);
      }
    } catch (err) {
      console.error('Failed to load assessments:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load assessments');
    } finally {
      setLoadingAssessments(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadAssessments();
  }, [isAuthenticated]);

  // Loading state
  if (authLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: 'var(--slate-500)' }}>Loading...</p>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div>
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 8 }}>
            Trainer Dashboard
          </h1>
        </div>
        <div
          style={{
            padding: '16px 20px',
            background: 'var(--red-50)',
            border: '1px solid var(--red-100)',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <iconify-icon icon="lucide:alert-circle" width="20" height="20" style={{ color: 'var(--red-500)' }}></iconify-icon>
          <span style={{ color: 'var(--red-700)' }}>Please sign in to access the trainer console.</span>
        </div>
      </div>
    );
  }

  // Count assessments by status
  const draftCount = assessments.filter(a => a.status === 'draft').length;
  const publishedCount = assessments.filter(a => a.status === 'pending' || a.status === 'complete').length;

  return (
    <div>
      {/* Personalized Welcome */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 8 }}>
          {getGreeting()}, {firstName}!
        </h1>
        <p style={{ color: 'var(--slate-500)', fontSize: 15 }}>
          Ready to create engaging assessments for your learners?
        </p>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <div
          style={{
            padding: 20,
            background: 'white',
            borderRadius: 16,
            border: '1px solid var(--slate-200)',
          }}
        >
          <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--slate-900)', marginBottom: 4 }}>
            {assessments.length}
          </div>
          <div style={{ fontSize: 14, color: 'var(--slate-500)' }}>Total Assessments</div>
        </div>
        <div
          style={{
            padding: 20,
            background: 'white',
            borderRadius: 16,
            border: '1px solid var(--slate-200)',
          }}
        >
          <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--slate-900)', marginBottom: 4 }}>
            {draftCount}
          </div>
          <div style={{ fontSize: 14, color: 'var(--slate-500)' }}>In Progress</div>
        </div>
        <div
          style={{
            padding: 20,
            background: 'white',
            borderRadius: 16,
            border: '1px solid var(--slate-200)',
          }}
        >
          <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--slate-900)', marginBottom: 4 }}>
            {publishedCount}
          </div>
          <div style={{ fontSize: 14, color: 'var(--slate-500)' }}>Published</div>
        </div>
      </div>

      {/* Primary Action */}
      <div
        style={{
          padding: 32,
          background: 'var(--sky-500)',
          borderRadius: 20,
          marginBottom: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          boxShadow: '0 10px 15px -3px rgba(14, 165, 233, 0.3)',
        }}
      >
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: 'white', marginBottom: 8 }}>
            Create New Assessment
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, maxWidth: 400 }}>
            Let AI help you build a professional assessment in minutes. Just describe what you want to evaluate.
          </p>
        </div>
        <Link
          to="/console/trainer/assessments/new"
          style={{
            padding: '14px 28px',
            background: 'white',
            color: 'var(--sky-600)',
            borderRadius: 12,
            fontWeight: 600,
            fontSize: 15,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
            whiteSpace: 'nowrap',
          }}
        >
          <iconify-icon icon="lucide:sparkles" width="18" height="18"></iconify-icon>
          Start Creating
        </Link>
      </div>

      {/* Recent Assessments */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--slate-900)' }}>
            Recent Assessments
          </h2>
          <Link
            to="/console/trainer/assessments"
            style={{ fontSize: 14, color: 'var(--sky-600)', textDecoration: 'none' }}
          >
            View all &rarr;
          </Link>
        </div>

        {loadingAssessments ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--slate-500)' }}>
            Loading assessments...
          </div>
        ) : loadError ? (
          <div
            style={{
              padding: 24,
              background: 'var(--red-50)',
              borderRadius: 16,
              border: '1px solid var(--red-100)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <iconify-icon
                icon="lucide:alert-circle"
                width="20"
                height="20"
                style={{ color: 'var(--red-500)', flexShrink: 0, marginTop: 2 }}
              ></iconify-icon>
              <div style={{ flex: 1 }}>
                <p style={{ color: 'var(--red-700)', margin: 0, fontWeight: 500 }}>
                  Failed to load assessments
                </p>
                <p style={{ color: 'var(--red-600)', margin: '4px 0 12px', fontSize: 13 }}>
                  {loadError}
                </p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={loadAssessments}
                  style={{ padding: '8px 16px', fontSize: 13 }}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        ) : assessments.length === 0 ? (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              background: 'var(--slate-50)',
              borderRadius: 16,
              border: '1px dashed var(--slate-200)',
            }}
          >
            <iconify-icon
              icon="lucide:clipboard-list"
              width="40"
              height="40"
              style={{ color: 'var(--slate-300)', marginBottom: 12 }}
            ></iconify-icon>
            <p style={{ color: 'var(--slate-500)', marginBottom: 16 }}>
              You haven't created any assessments yet.
            </p>
            <Link to="/console/trainer/assessments/new" className="btn btn-primary">
              Create Your First Assessment
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {assessments.map((assessment) => (
              <Link
                key={assessment.id}
                to={`/console/trainer/assessments/${assessment.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  background: 'white',
                  borderRadius: 12,
                  border: '1px solid var(--slate-200)',
                  textDecoration: 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'var(--sky-300)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(14, 165, 233, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'var(--slate-200)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: 'var(--slate-900)' }}>
                      {(assessment.metadata as Record<string, unknown>)?.name as string || `Assessment ${assessment.id.slice(0, 8)}`}
                    </span>
                    <StatusBadge status={assessment.status} />
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--slate-500)' }}>
                    {assessment.assessmentMode === 'voice' ? 'Voice Conversation' : 'Quiz'} &middot;{' '}
                    Created {formatDate(assessment.createdAt)}
                  </div>
                </div>
                <iconify-icon
                  icon="lucide:chevron-right"
                  width="20"
                  height="20"
                  style={{ color: 'var(--slate-400)' }}
                ></iconify-icon>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Status badge component.
 */
function StatusBadge({ status }: { status: string }) {
  const defaultConfig = { bg: 'var(--amber-100)', color: 'var(--amber-700)', label: 'Draft' };

  const config: Record<string, { bg: string; color: string; label: string }> = {
    draft: defaultConfig,
    pending: { bg: 'var(--sky-100)', color: 'var(--sky-700)', label: 'Active' },
    complete: { bg: 'var(--emerald-100)', color: 'var(--emerald-700)', label: 'Complete' },
    invalidated: { bg: 'var(--slate-100)', color: 'var(--slate-500)', label: 'Archived' },
  };

  const { bg, color, label } = config[status] ?? defaultConfig;

  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        background: bg,
        color: color,
        textTransform: 'uppercase',
        letterSpacing: '0.025em',
      }}
    >
      {label}
    </span>
  );
}

/**
 * Format date for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'today';
  } else if (diffDays === 1) {
    return 'yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}
